import React from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * ErrorBoundary — production-grade error boundary
 *
 * - Catches render errors in child components
 * - Displays a user-friendly error message (no stack traces)
 * - Provides a "Refresh" button and optional "Try Again" reset
 * - Accessible: uses semantic HTML and ARIA attributes
 * - Logs errors to console for debugging (stripped in production builds)
 */
export default class ErrorBoundary extends React.Component<
  { children: React.ReactNode; onReset?: () => void },
  State
> {
  constructor(props: { children: React.ReactNode; onReset?: () => void }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ errorInfo: info });
    // Log to console — in production, this should be sent to an error tracking service
    console.error('ErrorBoundary caught a render error:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-dvh bg-slate-50 flex flex-col items-center justify-center p-8 font-sans"
          role="alert"
          aria-live="assertive"
        >
          <div className="max-w-lg w-full bg-white border border-red-200 rounded-2xl p-8 shadow-lg space-y-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 shrink-0">
                <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-lg font-bold text-slate-900">Something went wrong</h1>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              The application encountered an unexpected error. Please try refreshing the page.
              If the problem persists, contact our support team.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 py-2.5 btn-primary  text-white rounded-xl text-sm font-semibold transition-all cursor-pointer active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-brand-primary/15"
                aria-label="Refresh the page to retry"
              >
                Refresh Page
              </button>
              <button
                onClick={this.handleReset}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold transition-all cursor-pointer active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-slate-400/15"
                aria-label="Try again without refreshing"
              >
                Try Again
              </button>
              <button
                onClick={() => { signOut(auth).then(() => window.location.reload()); }}
                className="flex-1 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl text-sm font-semibold transition-all cursor-pointer active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-slate-400/15"
                aria-label="Sign out and reload the page"
              >
                Sign Out &amp; Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
